import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Calendar, User } from "lucide-react";
import { getNewsBySlug, getSettings, resolveMedia } from "../lib/api";
import { useLang } from "../lib/i18n";

export default function NewsDetailPage() {
  const { slug } = useParams();
  const { lang, t, lp } = useLang();
  const [item, setItem] = useState(null);
  const [settings, setSettings] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false;
    setItem(null); setError(null);
    Promise.all([getNewsBySlug(slug, lang), getSettings(lang)])
      .then(([n, s]) => { if (!cancel) { setItem(n); setSettings(s); } })
      .catch((e) => !cancel && setError(e?.response?.status === 404 ? "404" : "error"));
    return () => { cancel = true; };
  }, [slug, lang]);

  if (error === "404") return <div className="container-x py-32 text-center"><h1 className="text-3xl font-bold text-navy-dark">{t("newsNotFound")}</h1></div>;
  if (!item) return <div className="container-x py-32 text-center text-ink-muted">{t("loading")}</div>;

  const prefix = lang === "en" ? "/en" : "";
  const siteUrl = (settings.site_url || "https://preventum-diez.de").replace(/\/+$/, "");
  const deUrl = `${siteUrl}/news/${item.slug}/`;
  const enUrl = `${siteUrl}/en/news/${item.slug}/`;
  const url = lang === "en" ? enUrl : deUrl;
  const absoluteMediaUrl = (value) => {
    if (!value || /^https?:\/\//i.test(value)) return value || "";
    return `${siteUrl}${value.startsWith("/") ? "" : "/"}${value}`;
  };

  const rawTitle = (item.seo_title_de || item.title_de || "").trim();
  const pageTitle = /\|\s*Preventum Diez$/i.test(rawTitle)
    ? rawTitle
    : `${rawTitle} | Preventum Diez`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: item.title_de,
    description: item.teaser_de,
    image: absoluteMediaUrl(item.hero_image_url),
    datePublished: item.published_at,
    dateModified: item.updated_at,
    author: { "@type": "Person", name: item.author_name || "Praxis" },
    mainEntityOfPage: url,
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={item.meta_description_de || item.teaser_de || ""} />
        <link rel="canonical" href={url} />
        <meta name="robots" content="index,follow" />
        <link rel="alternate" hrefLang="de" href={deUrl} />
        <link rel="alternate" hrefLang="en" href={enUrl} />
        <link rel="alternate" hrefLang="x-default" href={deUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={item.title_de} />
        <meta property="og:description" content={item.teaser_de || ""} />
        <meta property="og:url" content={url} />
        {(item.og_image_url || item.hero_image_url) && (
          <meta property="og:image" content={absoluteMediaUrl(item.og_image_url || item.hero_image_url)} />
        )}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <article className="container-x max-w-3xl py-12 md:py-20">
        <Link to={lp("/news/")} className="inline-flex items-center gap-2 text-navy hover:text-brandred text-sm font-semibold mb-6">
          <ArrowLeft className="h-4 w-4" /> {t("allNews")}
        </Link>
        <div className="flex items-center gap-4 text-sm text-ink-muted mb-4">
          {item.category && <span className="uppercase tracking-wider font-semibold text-brandred">{item.category}</span>}
          {item.published_at && <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {item.published_at.slice(0,10)}</span>}
          {item.author_name && <span className="inline-flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> {item.author_name}</span>}
        </div>
        <h1 className="font-display text-display-xl text-navy-dark mb-5">{item.title_de}</h1>
        {item.teaser_de && <p className="text-xl text-ink-muted mb-8 leading-relaxed">{item.teaser_de}</p>}
        {item.hero_video_url ? (
          <video src={resolveMedia(item.hero_video_url)} poster={resolveMedia(item.video_poster_url || item.hero_image_url)} controls playsInline className="w-full aspect-video rounded-2xl mb-8" />
        ) : item.hero_image_url ? (
          <img src={resolveMedia(item.hero_image_url)} alt={item.title_de} className="w-full aspect-[16/9] object-cover rounded-2xl mb-8" />
        ) : null}
        <div className="prose-medical news-body">
          {/<[a-z][\s\S]*>/i.test(item.body_de || "") ? (
            <div dangerouslySetInnerHTML={{ __html: item.body_de }} />
          ) : (
            (item.body_de || "").split("\n\n").map((p, i) => (
              <p key={i} dangerouslySetInnerHTML={{ __html: p.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>") }} />
            ))
          )}
        </div>
      </article>
    </>
  );
}
